import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, type Variants } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  FileDown,
  FilePlus2,
  FileUp,
  History,
  Landmark,
  MapPinPlus,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { BadgeStatus } from "@/components/ui/badge-status";
import { SpatialPlanWizard } from "@/features/feasibility/SpatialPlanWizard";
import { fetchCorpusStats, fetchMunicipalities } from "@/api/client";
import type { CorpusStats } from "@/api/schemas";
import { useProjectStore } from "@/features/projects/useProjectStore";
import { cn } from "@/lib/utils";

/**
 * Landing screen — a MODULE CHOOSER, not an intake (Lukin feedback
 * 14.07.): the visitor decides which job they came for, or continues
 * where they left off. No upload here — every document lands on the
 * Donesi step, which owns the one basket + the console.
 *
 * Visual direction (Lukin "uljepšaj" pass): the header's brand navy
 * extends into a hero with a faint cadastral-parcel line pattern (our
 * literal domain), and the two module cards FLOAT over its bottom edge.
 * The primary card SHOWS the product — a miniature of the Donesi console
 * — instead of describing it in prose.
 */
export function LandingPage() {
  const navigate = useNavigate();
  const newProject = useProjectStore((s) => s.newProject);
  const setActive = useProjectStore((s) => s.setActive);
  const patchActive = useProjectStore((s) => s.patchActive);
  const projects = useProjectStore((s) => s.projects);

  const [planWizardOpen, setPlanWizardOpen] = useState(false);

  // Live corpus counts for the proof bar. Cached server-side for 60 s so
  // a viral landing-page hit doesn't fan out to the DB; ``staleTime``
  // mirrors the backend cache.
  const { data: corpus } = useQuery({
    queryKey: ["corpus-stats"],
    queryFn: fetchCorpusStats,
    staleTime: 60_000,
    retry: 1,
  });

  // Covered municipalities for the plans section — shares the cache key
  // the feasibility panel uses, so an activation there refreshes here too.
  const { data: munis } = useQuery({
    queryKey: ["municipalities"],
    queryFn: fetchMunicipalities,
    staleTime: 60_000,
    retry: 1,
  });

  function startProject() {
    const id = newProject();
    setActive(id);
    patchActive({ currentStep: 1 });
    navigate(`/project/${id}/donesi`);
  }

  const proof = [
    municipalitiesLabel(corpus),
    rulesLabel(corpus),
    snapshotLabel(corpus),
    gtcLabel(corpus),
  ].filter((l): l is string => l !== null);

  const muniList = munis?.municipalities ?? [];
  const muniShown = muniList.slice(0, 4);
  const muniMore = muniList.length - muniShown.length;

  return (
    <div className="mx-auto max-w-4xl">
      {/* ------------------------------------------------------------------
          HERO — the header's navy bleeds full-width into the page, over a
          faint cadastral-parcel pattern; the module cards float across its
          bottom edge (negative margin below). */}
      <section className="relative -mt-6 mx-[calc(50%-50vw)] overflow-hidden bg-ind-950 pb-32 pt-12 text-center">
        <ParcelPattern />
        <div className="relative mx-auto max-w-4xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-amber-400">
              <Sparkles className="h-3 w-3" />
              Provjera gradivosti + glavni projekt
            </div>
            <h1 className="text-[2.1rem] font-bold leading-[1.08] tracking-[-0.015em] text-white md:text-[2.8rem]">
              Gradivost i glavni projekt —{" "}
              <span className="text-amber-400">iz istih podataka.</span>
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-[1rem] leading-relaxed text-white/60">
              Verdikt s citatom članka plana, pa iz istih ulaza opći i
              tekstualni dio glavnog projekta. Bez prepisivanja.
            </p>

            {proof.length > 0 && (
              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[0.78rem]">
                {proof.map((label, i) => (
                  <span key={label} className="contents">
                    {i > 0 && (
                      <span className="select-none text-amber-500/70" aria-hidden>
                        ·
                      </span>
                    )}
                    <ProofItem>{label}</ProofItem>
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------------------------
          TWO DOORS — floating over the hero's edge. A fork of two jobs
          (documents vs verdict); the doors only CHOOSE, the work lives
          inside each module. */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
        }}
        className="relative z-10 -mt-24 grid items-stretch gap-4 md:grid-cols-[3fr_2fr]"
      >
        {/* ---- LEFT: GENERIRAJ (primary) ---- */}
        <motion.div
          variants={cardReveal}
          className="flex flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_24px_60px_-20px_rgba(30,27,75,0.45)]"
        >
          <div className="flex items-center gap-3 border-b border-stone-100 px-5 py-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-ind-100 text-ind-700">
              <FileUp className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-[0.98rem] font-bold text-stone-800">
                Generiraj projekt
              </div>
              <div className="text-[0.75rem] text-stone-400">
                nacrt + dokumenti → opći i tekstualni dio
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-4 p-5">
            {/* Show, don't tell: a miniature of the Donesi console. */}
            <KonzolaPeek />

            <ol className="grid gap-2.5">
              <StepItem
                n={1}
                icon={<FileUp className="h-3.5 w-3.5" />}
                title="Donesi"
                text="istovari nacrt i PDF-ove — sustav ih prepozna i razvrsta po recima"
              />
              <StepItem
                n={2}
                icon={<ClipboardCheck className="h-3.5 w-3.5" />}
                title="Provjeri"
                text="pregledaš pročitane podatke, dopuniš što fali"
              />
              <StepItem
                n={3}
                icon={<FileDown className="h-3.5 w-3.5" />}
                title="Preuzmi"
                text="Mapa 1 kao jedan uvezani PDF sa sadržajem"
              />
            </ol>
          </div>

          <div className="mt-auto border-t border-stone-100 bg-stone-50/70 px-4 py-3">
            <Button size="lg" onClick={startProject} className="w-full px-6">
              <FilePlus2 className="h-4 w-4" />
              Novi projekt
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>

        {/* ---- RIGHT: PROVJERI GRADIVOST (the wedge) + plan coverage ---- */}
        <motion.div
          variants={cardReveal}
          className="flex flex-col rounded-2xl border border-ind-200 bg-white p-5 shadow-[0_24px_60px_-20px_rgba(30,27,75,0.35)]"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-ind-100 text-ind-700">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div className="text-[0.98rem] font-bold text-stone-800">
              Provjeri gradivost
            </div>
          </div>
          <p className="mt-2 text-[0.85rem] leading-snug text-stone-600">
            Smije li se uopće graditi na čestici? Verdikt s limitima plana i
            citatom članka — bez učitavanja dokumenata, u minuti.
          </p>

          {/* Concrete teaser: the verdict shape, so the wedge is legible
              before a click. Labelled "primjer" so it's never mistaken for
              a live result. */}
          <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50/70 p-3">
            <div className="text-[0.66rem] font-semibold uppercase tracking-[0.06em] text-stone-400">
              Primjer
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[0.82rem] text-stone-700">
                M1 · KIG 0,4
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-stone-400" />
              <BadgeStatus kind="warn">Dopušteno uz uvjete</BadgeStatus>
            </div>
            <div className="mt-1 text-[0.72rem] text-stone-400">
              limiti plana · čl. 64
            </div>
          </div>

          <Button
            variant="outline"
            className="mt-3 w-full border-ind-300 text-ind-700 hover:border-ind-500 hover:bg-ind-50"
            onClick={() => navigate("/provjera")}
          >
            Provjeri gradivost
            <ArrowRight className="h-4 w-4" />
          </Button>

          {/* Plan coverage lives here — it's the trust that makes the verdict
              credible, and the door to add a missing općina. */}
          <div className="mt-4 border-t border-stone-100 pt-4">
            <div className="flex items-center gap-2">
              <Landmark className="h-4 w-4 text-ind-600" />
              <h3 className="text-[0.72rem] font-bold uppercase tracking-[0.05em] text-stone-500">
                Prostorni planovi u bazi
              </h3>
            </div>
            {muniShown.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {muniShown.map((m) => (
                  <span
                    key={m.code}
                    className="rounded-full border border-stone-200 bg-white px-2 py-0.5 text-[0.74rem] font-medium text-stone-600"
                  >
                    {m.name}
                  </span>
                ))}
                {muniMore > 0 && (
                  <span className="rounded-full border border-dashed border-stone-300 px-2 py-0.5 text-[0.74rem] font-medium text-stone-400">
                    + još {muniMore}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setPlanWizardOpen((v) => !v);
                window.setTimeout(() => {
                  document
                    .getElementById("plan-wizard")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 30);
              }}
              className="mt-2 inline-flex items-center gap-1.5 text-[0.78rem] font-semibold text-ind-700 hover:text-ind-800"
            >
              <MapPinPlus className="h-3.5 w-3.5" />
              {planWizardOpen ? "Sakrij" : "Nema tvoje općine? Učitaj plan"}
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Plan uploader — full width when open (it needs the room). */}
      {planWizardOpen && (
        <div
          id="plan-wizard"
          className="mt-4 scroll-mt-24 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm"
        >
          <p className="mb-2 text-[0.82rem] text-stone-500">
            Učitaj PDF odredbenog dijela plana — sustav izvuče limite s citatima
            članaka, ti ih potvrdiš i općina je odmah spremna za provjeru.
          </p>
          <SpatialPlanWizard
            onClose={() => setPlanWizardOpen(false)}
            onActivated={() => {
              // Coverage chips refresh via the shared "municipalities" query
              // key the wizard already invalidates.
            }}
          />
        </div>
      )}

      {/* Returning users — where they left off, with visible progress. */}
      {projects.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.4, ease: "easeOut" }}
          className="mt-6"
        >
          <div className="mb-2 flex items-center gap-2">
            <History className="h-4 w-4 text-stone-400" />
            <h3 className="text-[0.72rem] font-bold uppercase tracking-[0.05em] text-stone-500">
              Nastavi rad
            </h3>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {projects.slice(0, 6).map((p) => {
              const step = (p.currentStep ?? 1) as 1 | 2 | 3;
              return (
                <motion.button
                  key={p.id}
                  type="button"
                  whileHover={{ y: -2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-left shadow-sm transition-colors hover:border-ind-300"
                  onClick={() => {
                    setActive(p.id);
                    const seg =
                      step === 3 ? "preuzmi" : step === 2 ? "provjeri" : "donesi";
                    navigate(`/project/${p.id}/${seg}`);
                  }}
                >
                  <div className="min-w-0">
                    <div
                      className="truncate text-[0.86rem] font-semibold text-stone-800"
                      title={p.name}
                    >
                      {truncateMiddle(p.name, 30)}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <StepDots step={step} />
                      <span className="text-[0.7rem] font-semibold text-ind-700">
                        {step === 3 ? "Preuzmi" : step === 2 ? "Provjeri" : "Donesi"}
                      </span>
                      <span className="text-[0.7rem] text-stone-400">
                        {new Date(p.updatedAt).toLocaleDateString("hr-HR")}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-stone-300" />
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
}

const cardReveal: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

/**
 * Faint cadastral-parcel line work behind the hero — literally our domain.
 * Thin white polygon borders at ~7 % opacity + one amber "regulacijska
 * linija" accent; decorative only.
 */
function ParcelPattern() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 1440 460"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <g fill="none" stroke="white" strokeOpacity="0.07" strokeWidth="1.2">
        <path d="M-20 90 L210 40 L320 150 L180 240 L-40 200 Z" />
        <path d="M210 40 L470 -30 L560 90 L320 150 Z" />
        <path d="M320 150 L560 90 L640 230 L430 310 L180 240 Z" />
        <path d="M-40 200 L180 240 L150 400 L-60 380 Z" />
        <path d="M180 240 L430 310 L390 470 L150 400 Z" />
        <path d="M1040 30 L1290 -20 L1420 110 L1180 170 Z" />
        <path d="M1180 170 L1420 110 L1500 260 L1280 320 Z" />
        <path d="M980 180 L1180 170 L1280 320 L1060 400 L920 300 Z" />
        <path d="M1060 400 L1280 320 L1340 480 L1100 500 Z" />
        <path d="M860 60 L1040 30 L1180 170 L980 180 Z" />
      </g>
      {/* regulacijska linija */}
      <path
        d="M-20 330 C 300 280, 620 350, 900 290 S 1360 240, 1500 300"
        fill="none"
        stroke="#d97706"
        strokeOpacity="0.25"
        strokeWidth="1.5"
        strokeDasharray="7 6"
      />
      <g fill="white" fillOpacity="0.06">
        <circle cx="210" cy="40" r="2.5" />
        <circle cx="320" cy="150" r="2.5" />
        <circle cx="180" cy="240" r="2.5" />
        <circle cx="1180" cy="170" r="2.5" />
        <circle cx="1060" cy="400" r="2.5" />
      </g>
    </svg>
  );
}

/** Miniature of the Donesi console — decorative product peek. */
function KonzolaPeek() {
  return (
    <div className="pointer-events-none select-none rounded-xl border border-stone-200 bg-stone-50/60 p-3">
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-[0.64rem] font-bold uppercase tracking-[0.05em] text-stone-500">
          Što projekt treba
        </span>
        <span className="text-[0.64rem] text-stone-400">
          6 spremno ·{" "}
          <span className="font-semibold text-amber-700">2 fali</span>
        </span>
      </div>
      <ul className="flex flex-col gap-1">
        <PeekRow ok label="Situacija" value="01_sira.pdf · 97 %" />
        <PeekRow ok label="Sudski registar" value="sudski.pdf · opći dio 1.1" />
        <PeekRow label="HKA rješenje" value="povuci PDF ovamo" missing />
        <PeekRow label="Investitor" value="upiši ovdje" missing />
        <li className="flex items-center gap-1.5 pt-0.5 text-[0.72rem] text-stone-500">
          <Sparkles className="h-3 w-3 shrink-0 text-ind-400" />5 izjava
          aplikacija generira sama
        </li>
      </ul>
    </div>
  );
}

function PeekRow({
  ok,
  missing,
  label,
  value,
}: {
  ok?: boolean;
  missing?: boolean;
  label: string;
  value: string;
}) {
  return (
    <li className="flex items-center gap-1.5 text-[0.74rem]">
      {ok ? (
        <CheckCircle2 className="h-3 w-3 shrink-0 text-green-600" />
      ) : (
        <Circle className="h-3 w-3 shrink-0 text-amber-400" />
      )}
      <span className={cn("font-medium", ok ? "text-stone-700" : "text-stone-600")}>
        {label}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 truncate",
          missing ? "text-ind-600" : "text-stone-400",
        )}
      >
        {value}
      </span>
      {missing && (
        <span className="shrink-0 text-[0.68rem] font-semibold text-amber-700">
          — fali
        </span>
      )}
    </li>
  );
}

function StepItem({
  n,
  icon,
  title,
  text,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-ind-50 text-ind-700">
        {icon}
      </span>
      <span className="text-[0.82rem] leading-snug text-stone-600">
        <strong className="font-bold text-stone-800">
          {n} · {title}
        </strong>{" "}
        — {text}
      </span>
    </li>
  );
}

/** Three dots showing how far a draft got (● reached, ○ ahead). */
function StepDots({ step }: { step: 1 | 2 | 3 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden>
      {[1, 2, 3].map((s) => (
        <span
          key={s}
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            s <= step ? "bg-ind-600" : "bg-stone-200",
          )}
        />
      ))}
    </span>
  );
}

/** Middle-ellipsis so long technical names keep their distinctive tail
 * ("…bv-173 (1)") instead of truncating into identical prefixes. */
function truncateMiddle(name: string, max: number): string {
  if (name.length <= max) return name;
  const head = Math.ceil((max - 1) * 0.6);
  const tail = max - 1 - head;
  return `${name.slice(0, head)}…${name.slice(name.length - tail)}`;
}

/** Proof-bar item — bolds a leading number so the strip reads as evidence,
 * not a sentence. */
function ProofItem({ children }: { children: string }) {
  const m = /^([\d.,]+)\s+(.*)$/.exec(children);
  if (!m) return <span className="text-white/50">{children}</span>;
  return (
    <span className="text-white/50">
      <strong className="font-bold text-white/90">{m[1]}</strong> {m[2]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Proof-bar label helpers — numbers from the cached ``/api/corpus-stats``
// snapshot. Each returns ``null`` when there's nothing real to show; a proof
// bar of bare-noun placeholders undermines the trust it's meant to build.
// Kept number-leading so ``ProofItem`` can bold the figure.
// ---------------------------------------------------------------------------

function municipalitiesLabel(c: CorpusStats | undefined): string | null {
  const n = c?.municipalities;
  if (typeof n !== "number" || n <= 0) return null;
  return `${n} općina s pravilima`;
}

function rulesLabel(c: CorpusStats | undefined): string | null {
  const n = c?.construction_rules;
  if (typeof n !== "number" || n <= 0) return null;
  return `${n.toLocaleString("hr-HR")} limita s citatom članka`;
}

function snapshotLabel(c: CorpusStats | undefined): string | null {
  const n = c?.law_instruments_synced;
  if (typeof n !== "number" || n <= 0) return null;
  return `${n.toLocaleString("hr-HR")} propisa u bazi`;
}

function gtcLabel(c: CorpusStats | undefined): string | null {
  const n = c?.gtc_count;
  if (typeof n !== "number" || n <= 0) return null;
  return `${n} scenarija validirano`;
}
